<script setup lang="ts">
import type { AlgoliaVisualFiltersProps } from './AlgoliaVisualFilters.props'

import { dragscroll as vDragscroll } from 'vue-dragscroll'

const props = defineProps<AlgoliaVisualFiltersProps>()

const { breakpoint } = useProjectBreakpoint()
const isDesktop = breakpoint.greater('md')
const linkScrollableDiv = ref<HTMLElement>()

const { state: _state, instance } = useAisWidget('refinementList')
const { state: _currentRefinements } = useAisWidget('currentRefinements')
const state = computed(() => _state.value[props.facet])

const disjunctiveFacetValues = computed(
  () =>
    instance.value.helper?.lastResults?.disjunctiveFacets?.find(
      df => df.name === props.facet
    )?.data as Record<string, number> | undefined
)

const currentRefinements = computed(() => {
  return _currentRefinements.value.items
    .filter(i => i.attribute === props.facet)
    .flatMap(i => i.refinements)
})

const scrollHorizontally = (event: WheelEvent) => {
  const scWidth = linkScrollableDiv.value?.scrollWidth ?? 0
  const clWidth = linkScrollableDiv.value?.clientWidth ?? 0
  if (event.deltaX !== 0 || clWidth >= scWidth) return
  event.preventDefault()
  if (linkScrollableDiv.value) {
    linkScrollableDiv.value.scrollBy({
      left: event.deltaY,
    })
  }
}

const formatLabel = (facet: string, type: string, value: string) => {
  if (facet === 'MacroColorDesc' || type === 'swatch') {
    return value.split('_')[1] ?? value
  }
  if (facet === 'microCat') {
    return value.split(' > ').at(-1) ?? value
  }
  return value
}
</script>
<template>
  <div class="bg-background-secondary p-md flex justify-center">
    <div
      ref="linkScrollableDiv"
      v-dragscroll.x="{ active: isDesktop }"
      class="scrollable-visualfilters scrollbar-hide gap-xs md:gap-md flex scroll-m-3.5 overflow-y-hidden"
      @wheel="scrollHorizontally"
    >
      <template v-if="type === 'media'">
        <div v-for="facetValue in values" :key="facetValue.value">
          <!-- CARD TYPE (category) -->
          <UICarouselChipsMediaCard
            v-if="type === 'media'"
            class="w-[84px]"
            :chip="{
              label: facetValue.label ?? facetValue.value,
              media: facetValue.media,
              hoverMedia: facetValue.hoverMedia,
            }"
            :chip-selected="
              !!currentRefinements.find(
                (cr: any) => cr.value === facetValue.value
              )
            "
            :count="disjunctiveFacetValues?.[facetValue.value] ?? 0"
            @select="() => state.refine(facetValue.value)"
          />
        </div>
      </template>
      <template v-else-if="disjunctiveFacetValues">
        <div v-for="i in Object.entries(disjunctiveFacetValues)" :key="i[0]">
          <!-- TEXT TYPE (text-only)-->
          <UICarouselChipsText
            v-if="type === 'text'"
            :chip="{
              label: formatLabel(facet, type, i[0]),
            }"
            :count="i[1]"
            :chip-selected="
              !!currentRefinements.find((cr: any) => cr.value === i[0])
            "
            @selected="() => state.refine(i[0])"
          />
          <!-- SWATCH TYPE (color) -->
          <UICarouselChipsColor
            v-if="type === 'swatch'"
            :chip="{
              label: formatLabel(facet, type, i[0]),
              boxColor: '#' + i[0].split('_')[0],
            }"
            :count="i[1]"
            :chip-selected="
              !!currentRefinements.find((cr: any) => cr.value === i[0])
            "
            @select="() => state.refine(i[0])"
          />
        </div>
      </template>
    </div>
  </div>
</template>
